import React, { useEffect, useState, useContext, useCallback, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
/* --------------------------------------------- */
import { routeVarMap } from '../route'
/* --------------------------------------------- */
import utils from '../utils/utils'
import { hydratePageComponent } from '../utils/contentfulHandler'
import { splitLanguage } from '../utils/localStorageService'
/* --------------------------------------------- */
import Rail from '../components/Rail'
/* --------------------------------------------- */
import { GlobalContext } from '../context/global.context'
/* --------------------------------------------- */
import useFavApi from '../apis/useFavApi'
import useAnalytics from '../containers/useAnalytics'
import useGeneralApi from '../apis/useGeneralApi'
import useDetailsApi from '../apis/useDetailsApi'
/* --------------------------------------------- */
import useFirebaseTrace from '../hooks/useFirebaseTrace'
import useRecentlyWatched from '../hooks/useRecentlyWatched'
import useFavourites from '../hooks/useFavourites'
import useListing from '../hooks/useListing'
import useCatalog from '../hooks/useCatalog'
/* --------------------------------------------- */
import { getRecentlyWatchedLocal } from '../utils/sessionStorageService'
import useParseExtendedFeedQuery from '../hooks/useParseExtendedFeedQuery'
import useRecommendation from '../hooks/useRecommendation'
/* --------------------------------------------- */
const getPlaceholders = (curentSeasonEpisodeNumber) => {
  let placeHolderNumber = 3
  if (curentSeasonEpisodeNumber || curentSeasonEpisodeNumber === 0) placeHolderNumber = curentSeasonEpisodeNumber
  const items = []
  for (let i = 0; i < placeHolderNumber; i++) items.push({ isPlaceholder: true })
  return items
}
/* --------------------------------------------- */
// Container component which holds rail/grid data fetch logic and display logic.
const RailContainer = React.memo(({
  query = '', pageType, noDataCallback, mediaId = '', sysId = '', searchText = '', searchMode, maxResultCount, pageName, playbackProgress, identityProvider,
  curentSeason, isThisCREATE, onApiEnd, searchResultTypeRatio = '', seriesId = '', curentSeasonEpisodeNumber, detailsSectionData = {}, dataLoading, isRelatedRail, mapIndex,
  isPlayerRecommendationRail, playerRelatedComponent, watchLaterClick, type, uid
}) => {
  detailsSectionData = detailsSectionData || {}
  const params = useParams()
  /* --------------------------------------------- */
  query = (query || '').replace(/,/g, '&')
  /* --------------------------------------------- */
  const {
    railInfo: recentlyWatchedRailInfo,
    railItemsInfo: recentlyWatchedRailItemsInfo,
    isNoData: recentlyWatchedIsNoData
  } = useRecentlyWatched({ sysId, query, pageType, pageName })
  /* --------------------------------------------- */
  const {
    railInfo: favouritesRailInfo,
    railItemsInfo: favouritesRailItemsInfo,
    isNoData: favouritesIsNoData
  } = useFavourites({ sysId, query, pageType, pageName })
  /* --------------------------------------------- */
  const {
    railInfo: listingRailInfo,
    railItemsInfo: listingRailItemsInfo,
    isNoData: listingIsNoData
  } = useListing({ sysId, query, pageType })
  /* --------------------------------------------- */
  const {
    railInfo: catalogRailInfo,
    railItemsInfo: catalogRailItemsInfo,
    isNoData: catalogIsNoData
  } = useCatalog({ sysId, query, pageType, pageName, isThisCREATE, mediaId })
  /* --------------------------------------------- */
  const [railInfo, updateRailInfo] = useState({})
  const [railItemsInfo, updateRailItemsInfo] = useState([])
  const [isNoData, setIsNoData] = useState(false)
  const [currentPageNumber, updateCurrentPageNumber] = useState(1)
  const [isRailItemInfoLoaded, updateIsRailItemInfoLoaded] = useState(false)
  const isScrollPaginationRef = useRef(false)
  const pComponentRef = useRef({})
  const sComponentRef = useRef({})
  const railItemInfoRef = useRef(railItemsInfo)
  const railItemCountRef = useRef(0)
  /* --------------------------------------------- */
  const { isAuthenticated, appLanguage } = useContext(GlobalContext)
  /* --------------------------------------------- */
  const firebaseTrace = useFirebaseTrace()
  const parseExtendedFeedQuery = useParseExtendedFeedQuery()
  /* --------------------------------------------- */
  const {
    getListItems,
    getBestMatch,
    getChannelInfo
  } = useGeneralApi()
  const { getEpisodeDetails } = useDetailsApi()
  const { deleteFavourites } = useFavApi()
  /* --------------------------------------------- */
  const { sendAnalytics } = useAnalytics()
  /* --------------------------------------------- */
  const totalPages = useRef(1)
  /* --------------------------------------------- */
  const { appConfig, appMessages } = useSelector(store => store.appConfigReducers)
  const { searchComponents } = useSelector(store => store.search)
  const { catalogConfig = {} } = useSelector(store => store.catalog)
  const { pageComponent = {} } = useSelector(store => store.navigation)
  const { pageComponents = {} } = pageComponent
  const pComponent = pageComponents[sysId]
  const sComponent = searchComponents.component_search
  const {
    catalogTheme, catalogComponentThemeData,
    episodeConfig: eConfig = {},
    episodeComponentStyle: eStyle = [],
    episodeComponentTheme: eTheme = {},
    relatedComponent: rComp = {},
    rConfig: relatedRailContent,
  } = catalogConfig
  const catalogThemeRef = useRef(catalogTheme)
  const catalogComponentThemeDataRef = useRef(catalogComponentThemeData)
  const {
    railInfo: recommendationRailInfo,
    railItemsInfo: recommendationRailItemsInfo,
    isNoData: recommendationIsNoData,
    isLoading,
  } = useRecommendation({ sysId: sysId, query, pageType, pageName, mediaId, rComp, catalogThemeRef, catalogComponentThemeDataRef,
     isRelatedRail, isPlayerRecommendationRail, playerRelatedComponent, detailsSectionData, relatedRailContent,
     recommbeeAvailable: rComp?.contents?.[0]?.source === "recombee", seriesId, type, uid
    });
  /* --------------------------------------------- */

  /* --------------------------------------------- */
  /* --------------------------------------------- */
  useEffect(() => {
    updateRailInfo(recentlyWatchedRailInfo)
  }, [recentlyWatchedRailInfo])
  /* --------------------------------------------- */
  useEffect(() => {
    updateRailItemsInfo(recentlyWatchedRailItemsInfo)
  }, [recentlyWatchedRailItemsInfo])
  /* --------------------------------------------- */
  useEffect(() => {
    setIsNoData(recentlyWatchedIsNoData)
  }, [recentlyWatchedIsNoData])
  /* --------------------------------------------- */
  useEffect(() => {
    updateRailInfo(favouritesRailInfo)
  }, [favouritesRailInfo])
  /* --------------------------------------------- */
  useEffect(() => {
    updateRailItemsInfo(favouritesRailItemsInfo)
  }, [favouritesRailItemsInfo])
  /* --------------------------------------------- */
  useEffect(() => {
    setIsNoData(favouritesIsNoData)
  }, [favouritesIsNoData])
  /* --------------------------------------------- */
  useEffect(() => {
    updateRailInfo(listingRailInfo)
  }, [listingRailInfo])
  /* --------------------------------------------- */
  useEffect(() => {
    updateRailItemsInfo(listingRailItemsInfo)
  }, [listingRailItemsInfo])
  /* --------------------------------------------- */
  useEffect(() => {
    updateRailInfo(recommendationRailInfo);
  }, [recommendationRailInfo]);
  /* --------------------------------------------- */
  useEffect(() => {
    updateRailItemsInfo(recommendationRailItemsInfo);
  }, [recommendationRailItemsInfo]);
  /* --------------------------------------------- */
  useEffect(() => {
    setIsNoData(recommendationIsNoData);
  }, [recommendationIsNoData]);
  /* --------------------------------------------- */
  useEffect(() => {
    setIsNoData(listingIsNoData)
  }, [listingIsNoData])
  /* --------------------------------------------- */
  useEffect(() => {
    updateRailInfo(catalogRailInfo)
  }, [catalogRailInfo])
  /* --------------------------------------------- */
  useEffect(() => {
    updateRailItemsInfo(catalogRailItemsInfo)
  }, [catalogRailItemsInfo])
  /* --------------------------------------------- */
  useEffect(() => {
    setIsNoData(catalogIsNoData)
  }, [catalogIsNoData])
  /* --------------------------------------------- */
  const isScrollPaginate = (style) => {
    return !(utils.isNotNull(style, 0, 'type') && style[0].type === 'grid')
  }
  /* --------------------------------------------- */
  const nextPageCallback = useCallback((res, config = {}) => {
    let isMounted = true
    const { maxItems = null } = config || {}
    if (res) {
      totalPages.current = res.totalPages || 1
      const content = res.content || []
      /* --------------------------------------------- */
      if (!isMounted) return
      updateRailItemsInfo((railItemsInfo) => {
        let oldContent = [...railItemsInfo]
        oldContent = oldContent.filter(Boolean).filter(item => !item.isPlaceholder)
        const oldAndNewContent = [...oldContent, ...content]
        railItemCountRef.current = oldAndNewContent.length
        if (!oldAndNewContent.length) setIsNoData(true)
        if (config.onSuccess) config.onSuccess(oldAndNewContent.length)
        return oldAndNewContent
      })
    }
    /* --------------------------------------------- */
    if (!isMounted) return
    if (maxItems !== null && railItemCountRef.current >= maxItems) return
    updateCurrentPageNumber((currentPageNumber) => {
      if (!isScrollPaginationRef.current && currentPageNumber + 1 <= totalPages.current) {
        return currentPageNumber + 1
      } else {
        return currentPageNumber
      }
    })
    return () => {
      isMounted = false
    }
  }, [])
  /* --------------------------------------------- */
  const favRemoveBtnHandler = useCallback((uid, index, title, type, contentOfferId) => {
    const newArray = [...railItemsInfo]
    newArray.splice(index, 1)
    updateRailItemsInfo(newArray)
    if (!newArray.length && noDataCallback) noDataCallback(true)
    /* --------------------------------------------- */
    let isMounted = true
    const baseUrl = appConfig.baseUrl
    if (!baseUrl || !isAuthenticated) return
    /* --------------------------------------------- */
    deleteFavourites(baseUrl, uid).then(() => {
      if (!isMounted) return
      sendAnalytics({
        type: '_content.favourite',
        action: 'remove',
        attr: {
          content_offer_id: contentOfferId,
          content_id: uid,
          item_id: uid,
          content_title: title,
          content_type: type,
        }
      })
    }).catch(() => { })
    /* --------------------------------------------- */
    return () => {
      isMounted = false
    }
  }, [
    sendAnalytics,
    railItemsInfo,
    noDataCallback,
    isAuthenticated,
    deleteFavourites,
    appConfig.baseUrl
  ])
  /* --------------------------------------------- */
  useEffect(() => {
    pComponentRef.current = pComponent
  }, [pComponent])
  /* --------------------------------------------- */
  useEffect(() => {
    sComponentRef.current = sComponent
  }, [sComponent])
  /* --------------------------------------------- */
  useEffect(() => {
    railItemInfoRef.current = railItemsInfo
  }, [railItemsInfo])
  /* --------------------------------------------- */
  useEffect(() => {
    catalogComponentThemeDataRef.current = catalogComponentThemeData
  }, [catalogComponentThemeData])
  /* --------------------------------------------- */
  useEffect(() => {
    catalogThemeRef.current = catalogTheme
  }, [catalogTheme])
  /* --------------------------------------------- */
  useEffect(() => {
    if (isNoData) {
      updateRailItemsInfo([])
      if (noDataCallback) {
        noDataCallback(true)
      }
    }
  }, [isNoData, noDataCallback])
  /* --------------------------------------------- */
  /**
   * Search page rail
   */
  useEffect(() => {
    if (pageType !== 'search') return
    updateRailInfo(railInfo => {
      let title = ''
      if (searchMode && searchMode !== 'title') {
        title = searchText
      } else {
        title = appMessages.search_result_title
      }
      const newInfo = { title }
      if (sysId) {
        newInfo.sysId = sysId
      }
      return {
        ...railInfo,
        ...newInfo
      }
    })
  }, [
    sysId,
    pageType,
    searchMode,
    searchText,
    appMessages.search_result_title
  ])
  useEffect(() => {
    let isMounted = true
    let trace = null
    const sComponent = sComponentRef.current
    if (!appConfig.baseUrl) return
    if (!sComponent) return
    const _query = utils.parse(query)
    const { config = {}, componentStyle = [], theme = {} } = sComponent || {}
    const maxItems = _query.viewall ? null : (config || {}).maxItems
    if (pageType === 'search' || _query.fromPage === 'search') {
      let style = componentStyle.map((style) => {
        return { ...style }
      })
      if (currentPageNumber === 1) {
        if (style.length > 0 && pageType === 'listing') {
          style = style.map((style) => {
            style.type = 'grid'
            if (!_query.viewall) {
              style.showComponentTitle = true
              style.showItemTitle = true
              style.itemSize = 'medium'
              style.itemEdgeRadius = 0.25
              style.itemOrientation = 0.667
            }
            if (style.itemOrientation) {
              if (style.itemOrientation > 1) {
                style.maxItemTitleLines = 2 // landscape
              } else {
                style.maxItemTitleLines = 3 // portrait
              }
            }
            return { ...style }
          })
        }
        isScrollPaginationRef.current = isScrollPaginate(style)
        updateRailInfo(railInfo => {
          const title = railInfo.title || config.title || ''
          return {
            ...railInfo,
            ...config,
            maxItems,
            title,
            style,
            componentTheme: theme
          }
        })
      }
      updateRailItemsInfo(getPlaceholders())
      /* --------------------------------------------- */
      if (_query.fromPage === 'search') {
        let queryString = {
          ..._query,
          page: currentPageNumber,
          language: splitLanguage(appLanguage),
          size: config.apiPageSize || 50
        }
        if (queryString.title) {
          const title = queryString.title
          delete queryString.title
          delete queryString.sysId
          delete queryString.pageName
          updateRailInfo(railInfo => ({
            ...railInfo,
            title
          }))
        }
        queryString = utils.stringify(queryString)
        /* --------------------------------------------- */
        getListItems(appConfig.baseUrl, queryString).then((res = {}) => {
          if (!isMounted) return
          nextPageCallback(res, { ...config, maxItems })
        }).catch(() => {
          if (!isMounted) return
          /* --------------------------------------------- */
          if (noDataCallback) noDataCallback(true)
          updateRailItemsInfo([])
        })
      } else {
        trace = firebaseTrace('search_page_load', {
          searchTerm: searchText
        })
        const options = {
          query: window.encodeURIComponent(searchText),
          maxResultCount,
          language: splitLanguage(appLanguage),
          size: config.apiPageSize || 50
        }
        if (_query.recentSearch) options.source = 'recentSearch'
        if (searchMode) options.searchMode = searchMode
        else options.ratio = searchResultTypeRatio
        getBestMatch(appConfig.baseUrl, options).then((res = {}) => {
          if (!isMounted) return
          /* --------------------------------------------- */
          if (maxItems !== null && railItemCountRef.current >= maxItems) return
          /* --------------------------------------------- */
          config.onSuccess = (itemsLength) => {
            trace.addAttrs({ resultCount: itemsLength + '' })
            trace.stop()
            trace = null
          }
          /* --------------------------------------------- */
          nextPageCallback(res, { ...config, maxItems })
        }).catch(() => {
          if (!isMounted) return
          /* --------------------------------------------- */
          if (noDataCallback) noDataCallback(true)
          updateRailItemsInfo([])
          trace.stop()
          trace = null
        })
      }
    }
    return () => {
      isMounted = false
      if (trace) trace.stop()
    }
  }, [
    appConfig.baseUrl,
    appLanguage,
    currentPageNumber,
    getBestMatch,
    getListItems,
    maxResultCount,
    nextPageCallback,
    noDataCallback,
    pageType,
    query,
    searchMode,
    searchResultTypeRatio,
    searchText,
    firebaseTrace
  ])
  /* --------------------------------------------- */
  /**
   * Episode rail in details page
   */
  useEffect(() => {
    if (dataLoading) return
    if (isRelatedRail) return
    if (params.type !== routeVarMap.type.series) return
    if (!curentSeason) return
    if (!appConfig.baseUrl) return

    let isMounted = true
    let trace = null

    const railInfo = {
      ...eConfig,
      style: eStyle,
      componentTheme: eTheme
    }
    if (currentPageNumber === 1) {
      isScrollPaginationRef.current = isScrollPaginate(eStyle)
      updateIsRailItemInfoLoaded(false)
      updateRailInfo(railInfo)
      updateRailItemsInfo(getPlaceholders(curentSeasonEpisodeNumber))
    }

    trace = firebaseTrace('details_carousel_load', {
      contentId: mediaId || '',
      contentTitle: detailsSectionData.title || '',
      contentType: params.type || '',
      carouselId: eConfig.identifierExt || 'episodes',
    })

    let apiPageSize = 50
    if (railInfo.apiPageSize) apiPageSize = railInfo.apiPageSize
    const parsedParams = parseExtendedFeedQuery(railInfo.params || "", {
      series_id: mediaId,
      season_id: curentSeason,
      genre: (detailsSectionData.genre || [])[0] || '',
      director: ((detailsSectionData.director || [])[0] || {}).personName || '',
      cast: ((detailsSectionData.actor || [])[0] || {}).personName || '',
      media_type: params.type || '',
      category: detailsSectionData.categoryId || ''
    })
    const query = `language=${splitLanguage(appLanguage)}&size=${apiPageSize}&page=${currentPageNumber}&${parsedParams}`
    getEpisodeDetails(appConfig.baseUrl, query).then((res = {}) => {
      if (!isMounted) return null

      if (res.content) {
        res.content = res.content.map((item) => {
          item.isShowRailItemInfo = true
          item.seriesId = seriesId
          return item
        }).sort((a, b) => (a.episodeNumber > b.episodeNumber) ? 1 : -1)
        nextPageCallback(res, eConfig)
      } else {
        if (noDataCallback) noDataCallback(true)
        updateRailItemsInfo([])
      }

      onApiEnd()

      trace.stop()
      trace = null

      updateIsRailItemInfoLoaded(true)
    }).catch(() => {
      if (!isMounted) return

      if (noDataCallback) noDataCallback(true)
      updateRailItemsInfo([])
      onApiEnd()

      trace.stop()
      trace = null

      updateIsRailItemInfoLoaded(true)
    })

    return () => {
      isMounted = false
      if (trace) trace.stop()
    }
  }, [
    appConfig.baseUrl,
    appLanguage,
    curentSeason,
    seriesId,
    currentPageNumber,
    eConfig,
    eStyle,
    eTheme,
    curentSeasonEpisodeNumber,
    getEpisodeDetails,
    nextPageCallback,
    noDataCallback,
    onApiEnd,
    mediaId,
    detailsSectionData.title,
    isRelatedRail,
    dataLoading,
    params.type,
    firebaseTrace,
    detailsSectionData.actor,
    detailsSectionData.categoryId,
    detailsSectionData.director,
    detailsSectionData.genre,
    parseExtendedFeedQuery
  ])
  /* --------------------------------------------- */
  /**
   * Episode rail in details page Progress
   */
  useEffect(() => {
    if (params.type === routeVarMap.type.series && isRailItemInfoLoaded && playbackProgress && playbackProgress.length) {
      /* --------------------------------------------- */
      const railItemInfo = (railItemInfoRef.current || []).filter(Boolean).filter(item => !item.isPlaceholder)
      /* --------------------------------------------- */
      if (!railItemInfo.length) {
        updateRailItemsInfo([])
        return
      }
      /* --------------------------------------------- */
      const recentlyWated = getRecentlyWatchedLocal() || []
      const updatedDataSet = railItemInfo.map((item) => {
        const savedItem = recentlyWated.filter((recentItem) => {
          if (recentItem.type === 'episode' && recentItem.seriesUid === item.uid) {
            return item
          }
          return false
        })

        if (savedItem.length && savedItem[0]) {
          return { ...item, duration: savedItem[0].duration, progress: savedItem[0].progress }
        } else {
          let getProgress = null
          if (playbackProgress.length > 0) {
            getProgress = playbackProgress.find((data) => data.uid === item.uid)
            if (getProgress) { return { ...item, duration: getProgress.playbackProgress.duration, progress: getProgress.playbackProgress.progress } } else { return item }
          } else { return item }
        }
      })
      updateRailItemsInfo(updatedDataSet)
    }
  }, [
    params.type,
    playbackProgress,
    isRailItemInfoLoaded
  ])
  /* --------------------------------------------- */
  /**
   * Related rail in detail page
   */
  useEffect(() => {
    if (!mediaId) return
    if (!appMessages.details_related) return
    if (!isRelatedRail) return
    if (dataLoading) return
    updateRailInfo(railInfo => {
      return {
        ...railInfo,
        pageName,
        title: appMessages.details_related
      }
    })
  }, [
    pageName,
    mediaId,
    isRelatedRail,
    dataLoading,
    appMessages.details_related
  ])
  useEffect(() => {
    let isMounted = true
    let trace = null
    if (!appConfig.baseUrl) return
    if (!isRelatedRail) return
    if (dataLoading) return
    if (!params.type) return
    if (!mediaId) return
    if (utils.isNull(rComp)) return

    // let contentType = ''
    // if (params.type === routeVarMap.type.movie) contentType = 'movie'
    // else if (params.type === routeVarMap.type.series) contentType = 'series'
    // else if (params.type === routeVarMap.type.preview) contentType = 'preview'
    // else if (params.type === routeVarMap.type.channel) contentType = 'channel'
    // else if (params.type === routeVarMap.type.match) contentType = 'match'
    // else if (params.type === routeVarMap.type.sports) contentType = 'sports'
    // else if (params.type === routeVarMap.type.sportingevent) contentType = 'sportingevent'
    // else if (params.type === routeVarMap.type.sportingEvent) contentType = 'sportingEvent'

    const {
      config: rConfig,
      componentStyles: rStyle,
      componentTheme: rTheme
    } = hydratePageComponent(rComp, { contentType: params.type}, {
      defaultPageTheme: catalogThemeRef.current,
      defaultComponentTheme: catalogComponentThemeDataRef.current
    })
    if (rComp?.contents?.[0]?.source === "recombee") return // Beacuse its recombee and will be handled by useRecommensation hook
    const _query = utils.parse(query)
    const maxItems = _query.viewall ? null : (rConfig || {}).maxItems
    const railInfo = {
      ...rConfig,
      maxItems,
      style: rStyle,
      componentTheme: rTheme
    }

    trace = firebaseTrace('details_carousel_load', {
      contentId: mediaId || '',
      contentTitle: detailsSectionData.title || '',
      contentType: params.type || '',
      carouselId: rConfig.identifierExt || 'related'
    })

    if (currentPageNumber === 1) {
      isScrollPaginationRef.current = isScrollPaginate(rStyle)
      updateRailInfo(info => {
        return {
          ...info,
          ...railInfo,
          pageName
        }
      })
      updateRailItemsInfo(getPlaceholders())
    }

    if (params.type === routeVarMap.type.channel) {
      const queryString = utils.stringify({
        language: splitLanguage(appLanguage),
        page: currentPageNumber,
        size: rConfig.apiPageSize
      })
      getChannelInfo(appConfig.baseUrl, mediaId, queryString)
        .then((res = {}) => {
          if (!isMounted) return
          let { type = '', uid, genre = [], association = '', content } = res
          if (content && content.length > 0) {
            type = content[0].type || ''
            uid = content[0].uid
            genre = content[0].genre || []
            association = content[0].association || ''
          }

          /* --------------------------------------------- */
          // const setNoData = () => {
          //   noDataCallback(true)
          //   updateRailItemsInfo([])
          // }

          let apiPageSize = 50
          if (railInfo.apiPageSize) apiPageSize = railInfo.apiPageSize
          const parsedParams = parseExtendedFeedQuery(railInfo.params || "", {
            genre: genre[0] || '',
            media_type: type || '',
            association: association || '',
          })
          const queryString = `language=${splitLanguage(appLanguage)}&size=${apiPageSize}&${parsedParams}&page=${currentPageNumber}`

          // let queryString = `type=${type}`
          // if (type === 'tournament') return setNoData()
          // else if (type === 'channel') return setNoData()
          // else if (type === 'match') {
          //   if (!tournamentId) return setNoData()
          //   queryString += `&filterBy=tournament&filter=${tournamentId}`
          // } else if (type === 'sportingevent') {
          //   if (!sport) return setNoData()
          //   queryString += `&filterBy=sport&filter=${sport}`
          // } else if (type === 'sportingEvent') {
          //   if (!sport) return setNoData()
          //   queryString += `&filterBy=sport&filter=${sport}`
          // }
          // else if (type === 'movie') {
          //   if (!genre[0]) return setNoData()
          //   queryString += `&filterBy=genre&filter=${genre[0]}`
          // }
          // if (type === 'series') {
          //   queryString += `&filterBy=genre&filter=${genre[0]}`
          // }
          /* --------------------------------------------- */
          getListItems(appConfig.baseUrl, queryString).then((res = {}) => {
            if (!isMounted) return
            /* --------------------------------------------- */
            const { content = [] } = res
            res.content = content.filter((item) => {
              return item.uid !== uid
            })
            nextPageCallback(res, { ...rConfig, maxItems })
            trace.stop()
            trace = null
          }).catch(() => {
            if (!isMounted) return
            /* --------------------------------------------- */
            if (noDataCallback) noDataCallback(true)
            updateRailItemsInfo([])
            trace.stop()
            trace = null
          })
        })
        .catch(() => {
          if (!isMounted) return
          /* --------------------------------------------- */
          if (noDataCallback) noDataCallback(true)
          updateRailItemsInfo([])
          trace.stop()
          trace = null
        })
    } else {
      let type = detailsSectionData.type || ''
      let genre = detailsSectionData.genre || []
      let association = detailsSectionData.association || ''
      const content = detailsSectionData.content || null
      if (content && content.length > 0) {
        if (!type) type = content[0].type || ''
        if (!genre.length) genre = content[0].genre || []
        if (!association) association = content[0].association || ''
      }

      let pageSize = null
      if (utils.isNotNull(railInfo, "apiPageSize") && utils.isNotNull(maxItems)) {
        if (railInfo.apiPageSize > maxItems) {
          pageSize = maxItems
        } else {
          pageSize = railInfo.apiPageSize
        }
      } else if (utils.isNotNull(railInfo, "apiPageSize")) {
        pageSize = railInfo.apiPageSize
      } else if (utils.isNotNull(maxItems)) {
        pageSize = maxItems
      } else {
        pageSize = 50
      }
      pageSize = pageSize + 1 // for same details page item.

      const parsedParams = parseExtendedFeedQuery(railInfo.params || "", {
        media_id: mediaId,
        series_id: mediaId,
        genre: genre[0] || '',
        director: ((detailsSectionData.director || [])[0] || {}).personName || '',
        cast: ((detailsSectionData.actor || [])[0] || {}).personName || '',
        media_type: type || '',
        category: detailsSectionData.categoryId || '',
        association: association || ''
      })

      const queryString = `language=${splitLanguage(appLanguage)}&size=${pageSize}&${parsedParams}&page=${currentPageNumber}`

      // let queryString = `type=${type}`
      // if (type === 'tournament') return setNoData()
      // else if (type === 'channel') return setNoData()
      // else if (type === 'match') {
      //   if (!tournamentId) return setNoData()
      //   queryString += `&filterBy=tournament&filter=${tournamentId}`
      // } else if (type === 'sportingevent') {
      //   if (!sport) return setNoData()
      //   queryString += `&filterBy=sport&filter=${sport}`
      // } else if (type === 'sportingEvent') {
      //   if (!sport) return setNoData()
      //   queryString += `&filterBy=sport&filter=${sport}`
      // }
      // else if (type === 'movie') {
      //   if (!genre[0]) return setNoData()
      //   queryString += `&filterBy=genre&filter=${genre[0]}`
      // }
      // if (type === 'series') {
      //   queryString += `&filterBy=genre&filter=${genre[0]}`
      // }
      /* --------------------------------------------- */
      getListItems(appConfig.baseUrl, queryString).then((res = {}) => {
        if (!isMounted) return
        /* --------------------------------------------- */
        const { content = [] } = res
        res.content = content.filter((item) => {
          return item.uid !== mediaId
        })
        nextPageCallback(res, { ...rConfig, maxItems })
        trace.stop()
        trace = null
      }).catch(() => {
        if (!isMounted) return
        /* --------------------------------------------- */
        if (noDataCallback) noDataCallback(true)
        updateRailItemsInfo([])
        trace.stop()
        trace = null
      })
    }
    return () => {
      isMounted = false
      if (trace) trace.stop()
    }
  }, [
    pageName,
    appConfig.baseUrl,
    appLanguage,
    currentPageNumber,
    dataLoading,
    detailsSectionData.title,
    detailsSectionData.type,
    detailsSectionData.genre,
    detailsSectionData.association,
    detailsSectionData.content,
    getChannelInfo,
    getListItems,
    mediaId,
    nextPageCallback,
    noDataCallback,
    query,
    rComp,
    isRelatedRail,
    params.type,
    firebaseTrace,
    detailsSectionData.actor,
    detailsSectionData.categoryId,
    detailsSectionData.director,
    parseExtendedFeedQuery
  ])
  /* --------------------------------------------- */
  const { config = {} } = pComponent || {}
  const {
    config: rConfig,
    // componentStyles: rStyle,
    // componentTheme: rTheme
  } = hydratePageComponent(rComp, { contentType: params.type }, {
    defaultPageTheme: catalogThemeRef.current,
    defaultComponentTheme: catalogComponentThemeDataRef.current
  })
  const {
    config: prConfig,
    // componentStyles: rStyle,
    // componentTheme: rTheme
  } = hydratePageComponent(playerRelatedComponent, { contentType: params.type }, {
    defaultPageTheme: catalogThemeRef.current,
    defaultComponentTheme: catalogComponentThemeDataRef.current
  })
  const isRecommendation = (!isRelatedRail && !isPlayerRecommendationRail && config.identifierExt === "related") || (isRelatedRail && rComp?.contents?.[0]?.source === "recombee") || (isPlayerRecommendationRail && prConfig.identifierExt === "related");

  const renderPlaceHolderForRecombeeRail = isRecommendation  && isLoading
  const renderRailForRecombee = isRecommendation && railItemsInfo.length > 0 && !isLoading
  if (renderPlaceHolderForRecombeeRail) {
    
    return (
      <Rail
        {...{
          ...railInfo,
          items: getPlaceholders(),
          mapIndex,
        }}
      />
    );
  }

  if (renderRailForRecombee) {
      return <Rail
        {...{
          ...railInfo,
          nextPageCallback,
          searchMode,
          searchText,
          favRemoveBtnHandler,
          items: railItemsInfo,
          baseUrl: appConfig.baseUrl,
          contentType: prConfig.identifierExt || rConfig.identifierExt || config.identifierExt,
          isThisCREATE: false,
          identityProvider: undefined,
          watchLaterClick,
          isPlayerRecommendationRail
        }}
    />
  }

  if (Object.keys(railInfo).length <= 0 ||
    railItemsInfo.length <= 0) {
    return (
      <></>
    )
  } else {
    return (
      <Rail
        {...{
          ...railInfo,
          nextPageCallback,
          searchMode,
          searchText,
          favRemoveBtnHandler,
          items: railItemsInfo,
          // componentTheme: theme,
          baseUrl: appConfig.baseUrl,
          contentType: config.identifierExt,
          isThisCREATE,
          identityProvider
        }}
      />
    )
  }
})
/* --------------------------------------------- */
RailContainer.defaultProps = {
  noDataCallback: null,
  onApiEnd: () => { }
}
/* --------------------------------------------- */
export default RailContainer
/* --------------------------------------------- */
